// InteractionBuilder.jsx - Fixed to pass streamSource prop

import { get as getCookie } from 'es-cookie'
import React, { useState, useRef } from 'react'
import { withStyles } from '@material-ui/core/styles'
import { Stepper, Step, StepLabel, Typography, Box } from '@material-ui/core'
import { CoreButton } from 'src/components/core'
import ActivitySelection from './activity-selection'
import InteractiveBlocksGenerator from './ai-guide'
import SelectedBuilder from './selected-builder'
import ColorSelection from './color-selection'
import ChatInterface from './chat-interface'
import styles from './styles'
import { track } from '@amplitude/analytics-browser'
import { messageBoxHideAction } from 'src/reducers/message-box/action'
import { useDispatch } from 'react-redux'
import { useCourseSettings } from 'src/hooks/useCourseSettings'
import {
  AI_GENERATION_URL,
  NEW_BUILD_EXPECTED_LENGTH,
  createBuildProgressTracker,
  fetchBuildResult,
  getStreamErrorMessage,
  useInteractionMetadata,
} from './chat-interface/build-progress/utils'
import { generateUUID } from 'src/components/editor/components/custom-plugins/simple-box/utils'

const steps = ['Select Activity', 'Review Details']

const CustomStepIcon = ({ active, completed }) => {
  const color = completed ? '#10B981' : active ? '#015FFB' : '#E5E7EB'
  return (
    <div
      style={{
        width: 10,
        height: 10,
        borderRadius: '50%',
        backgroundColor: color,
        border: active ? '2px solid #015FFB' : 'none',
        boxSizing: 'border-box',
      }}
    />
  )
}

const InteractionBuilder = props => {
  const {
    data,
    sourceText,
    classes,
    position,
    aiInstructionConfirmSelection,
    handleCancel,
    mode = 'pageContent',
    selectedElementData = null,
    language,
  } = props

  const dispatch = useDispatch()
  const { value: languageValue } = useCourseSettings('language')
  const courseLanguage = languageValue?.selected || language

  const [activeStep, setActiveStep] = useState(0)
  const [selectedData, setSelectedData] = useState(null)
  const [selectedIndex, setSelectedIndex] = useState(null)
  const [colors, setColors] = useState({
    backgroundColor: '#DBDEE3',
    primaryColor: '#282F35',
  })

  const [isChatModalOpen, setIsChatModalOpen] = useState(false)
  const [isInitialLoading, setIsInitialLoading] = useState(false)
  const streamHandlersRef = useRef(null)
  // Groups the first build and the chat edits that follow it
  const sessionIdRef = useRef(generateUUID())
  const getInteractionMetadata = useInteractionMetadata()

  // LocalStorage key for this interaction session
  const getInteractionStorageKey = () => {
    const timestamp = Date.now()
    return `ai_interaction_builder_${mode}_${timestamp}`
  }

  // Save interaction data to localStorage
  const saveInteractionToStorage = (html, metadata = {}) => {
    if (html && html.trim()) {
      try {
        const storageData = {
          html: html,
          timestamp: new Date().toISOString(),
          mode: mode,
          selectedData: selectedData,
          colors: colors,
          sourceText: sourceText,
          metadata: metadata,
        }
        const key = getInteractionStorageKey()
        localStorage.setItem(key, JSON.stringify(storageData))
        return key
      } catch (error) {
        console.error('Failed to save interaction to localStorage:', error)
      }
    }
    return null
  }

  // Enhanced error handling for the stream
  const handleStreamError = (error, messageId, onRetry = null) => {
    console.error('Stream Error:', error)
    streamHandlersRef.current?.stopBuild()

    let errorMessage = `❌ **Error occurred during interaction generation**\n\n`

    if (error.message && error.message.includes('overloaded')) {
      errorMessage += `The AI service is currently overloaded. This is usually temporary.\n\n`
      errorMessage += `**What you can do:**\n`
      errorMessage += `• Wait 1-2 minutes and try again\n`
      errorMessage += `• Try a simpler description\n`
      errorMessage += `• Check if your internet connection is stable\n\n`
    } else if (error.name === 'TypeError' && error.message.includes('fetch')) {
      errorMessage += `Network connection error. Please check your internet connection and try again.\n\n`
    } else {
      errorMessage += `${error.message}\n\n`
      errorMessage += `**Troubleshooting:**\n`
      errorMessage += `• Try refreshing the page\n`
      errorMessage += `• Check your internet connection\n`
      errorMessage += `• Contact support if the issue persists\n\n`
    }

    // Add error message to chat
    const errorMessageObj = {
      id: messageId || Date.now(),
      text: errorMessage,
      isBot: true,
      timestamp: new Date(),
      isError: true,
      onRetry,
    }

    if (streamHandlersRef.current && streamHandlersRef.current.addMessage) {
      streamHandlersRef.current.addMessage(errorMessageObj)
    }

    setIsInitialLoading(false)
  }

  // Chat Modal Handlers
  const handleCloseChatModal = () => {
    dispatch(messageBoxHideAction())
    setIsChatModalOpen(false)
    setIsInitialLoading(false)
  }

  const handleStreamResponse = handlers => {
    streamHandlersRef.current = handlers
  }

  const handleSendMessage = (message, messageId) => {
    console.log('New message:', message, messageId)
  }

  // Stepper navigation handlers
  const handleNext = () => {
    if (activeStep === 0) {
      track('interactive_activity_selected', {
        type: selectedData?.interaction_type,
      })
    }
    setActiveStep(prevActiveStep => prevActiveStep + 1)
  }

  const handleBack = () => {
    setActiveStep(prevActiveStep => prevActiveStep - 1)
  }

  // Selection handlers
  const handleSelect = (item, index) => {
    setSelectedIndex(index)
    setSelectedData(item)
  }

  const handleColorsChange = newColors => {
    setColors(newColors)
  }

  // Fetches the finished build by id and shows it; the streamed HTML is never rendered
  const loadBuildResult = async (buildId, selectedData, sourceText, colors) => {
    streamHandlersRef.current?.finishBuild()

    try {
      const html = await fetchBuildResult(buildId)
      streamHandlersRef.current?.showBuildResult(html)

      saveInteractionToStorage(html, {
        selectedData: selectedData,
        colors: colors,
        sourceText: sourceText,
        generatedAt: new Date().toISOString(),
      })

      setIsInitialLoading(false)

      // Success message
      setTimeout(() => {
        const successMessage = {
          id: Date.now() + 999,
          text:
            '✏️ Your interaction is live and ready to use.\n\nWant to spice it up? Just tell me what to change. \n\n Are you happy with it? Click "Use this Interaction" to add it to course.',
          isBot: true,
          timestamp: new Date(),
          isSystemMessage: true,
        }

        if (streamHandlersRef.current && streamHandlersRef.current.addMessage) {
          streamHandlersRef.current.addMessage(successMessage)
        }
      }, 500)
    } catch (error) {
      handleStreamError(error, Date.now() + 1000, () =>
        loadBuildResult(buildId, selectedData, sourceText, colors),
      )
    }
  }

  const handleAIInstruction = async (selectedData, sourceText, colors) => {
    setIsInitialLoading(true)
    setIsChatModalOpen(true)
    streamHandlersRef.current?.startBuild()

    try {
      const buildId = generateUUID()
      const response = await fetch(
        `${AI_GENERATION_URL}/api/createInteractionSonnet55`,
        {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            Authorization: `Bearer ${getCookie('token')}`,
          },
          body: JSON.stringify({
            selected_data: selectedData,
            source_text:
              mode === 'guide' ? selectedData.description : sourceText,
            background_color: colors.backgroundColor,
            primary_color: colors.primaryColor,
            element_data: selectedElementData,
            language: courseLanguage,
            build_id: buildId,
            metadata: getInteractionMetadata(sessionIdRef.current),
          }),
        },
      )

      if (!response.ok) {
        // Parse error response
        let errorData
        try {
          const errorText = await response.text()
          errorData = JSON.parse(errorText)
        } catch {
          errorData = {
            message: `HTTP ${response.status}: ${response.statusText}`,
            type: 'http_error',
          }
        }

        // Check for specific error types
        if (errorData.type === 'overloaded_error' || response.status === 503) {
          throw new Error(
            'Service is currently overloaded. Please try again in a few moments.',
          )
        } else if (response.status === 429) {
          throw new Error(
            'Too many requests. Please wait a moment before trying again.',
          )
        } else if (response.status >= 500) {
          throw new Error('Server error occurred. Please try again later.')
        } else {
          throw new Error(
            errorData.message ||
              `Failed to generate interaction (${response.status})`,
          )
        }
      }

      // The stream only drives the progress; its HTML is buffered, never rendered
      const reader = response.body.getReader()
      const decoder = new TextDecoder()
      const progressTracker = createBuildProgressTracker(
        NEW_BUILD_EXPECTED_LENGTH,
      )
      let fullResponse = ''

      while (true) {
        const { done, value } = await reader.read()

        if (done) {
          break
        }

        const chunk = decoder.decode(value, { stream: true })
        fullResponse += chunk
        streamHandlersRef.current?.updateBuild(progressTracker.push(chunk))
      }

      const streamError = getStreamErrorMessage(fullResponse)
      if (streamError) {
        throw new Error(streamError)
      }

      await loadBuildResult(buildId, selectedData, sourceText, colors)
    } catch (error) {
      console.error('Interaction generation error:', error)
      handleStreamError(error, Date.now() + 1000)
    }
  }

  // Confirm handler
  const handleConfirm = () => {
    track('interactive_activity_confirm_selection')
    handleAIInstruction(selectedData, sourceText, colors)
  }

  // Guide mode handler
  const handleGuideProceeed = textContent => {
    setSelectedData({
      title: 'Custom Interactive Block',
      description: textContent,
      text: textContent,
    })
    handleNext()
  }

  // Step content renderer
  const getStepContent = step => {
    switch (step) {
      case 0:
        if (mode === 'guide') {
          return <InteractiveBlocksGenerator onProceed={handleGuideProceeed} />
        }
        return (
          <ActivitySelection
            data={data}
            selectedIndex={selectedIndex}
            selectedData={selectedData}
            onSelect={handleSelect}
          />
        )
      case 1:
        return (
          <ColorSelection
            selectedData={selectedData}
            onColorsChange={handleColorsChange}
            currentColors={colors}
          />
        )
      default:
        return 'Unknown step'
    }
  }

  const isStepValid = step => {
    switch (step) {
      case 0:
        if (mode === 'guide') {
          return selectedData !== null
        }
        return selectedData !== null
      case 1:
        return (
          selectedData !== null && colors.backgroundColor && colors.primaryColor
        )
      case 2:
        return true
      default:
        return false
    }
  }

  React.useEffect(() => {
    if (selectedElementData && mode === 'editInteraction') {
      setIsChatModalOpen(true)
      setIsInitialLoading(false)

      setTimeout(() => {
        const initialMessage = {
          id: Date.now(),
          text: `✏️ **Ready to edit your interactive element!**\n\nI can see:\n• Block Type: ${
            selectedElementData.blockType || 'Interaction Activity'
          }\n• Block ID: ${
            selectedElementData.blockId || 'N/A'
          }\n\n**What would you like me to change or improve?**\n\nYou can ask me to:\n• Modify colors, fonts, or styling\n• Change the content or layout\n• Add new features or interactions\n• Fix any issues you've noticed\n\nJust describe what you want and I'll make the changes!`,
          isBot: true,
          timestamp: new Date(),
          isSystemMessage: true,
        }

        if (streamHandlersRef.current && streamHandlersRef.current.addMessage) {
          streamHandlersRef.current.addMessage(initialMessage)
        }
      }, 500)
    }
  }, [selectedElementData, mode])

  return (
    <div className={classes.box}>
      {mode !== 'editInteraction' && (
        <>
          {/* Step Content */}
          <Box className={classes.stepContent}>
            {getStepContent(activeStep)}
          </Box>

          {/* Navigation Buttons */}
          {!(mode === 'guide' && activeStep === 0) && (
            <div className={classes.buttonContainer}>
              <div className={classes.textSection}>
                {activeStep === 0 && (
                  <Typography className={classes.note}>
                    AI assistance can make mistakes; please double-check
                    critical information.
                  </Typography>
                )}
              </div>
              <div className={classes.buttonGroup}>
                {activeStep > 0 && (
                  <CoreButton
                    onClick={handleBack}
                    size="medium"
                    color="secondary"
                    variant="text"
                  >
                    Back
                  </CoreButton>
                )}

                {activeStep < steps.length - 1 ? (
                  <CoreButton
                    size="medium"
                    disabled={!isStepValid(activeStep)}
                    color="primary"
                    variant="contained"
                    onClick={handleNext}
                  >
                    Next
                  </CoreButton>
                ) : (
                  <CoreButton
                    size="medium"
                    disabled={!isStepValid(activeStep)}
                    color="primary"
                    variant="contained"
                    onClick={handleConfirm}
                  >
                    Build the Interaction
                  </CoreButton>
                )}
              </div>
            </div>
          )}
        </>
      )}

      {/* Edit mode info */}
      {mode === 'editInteraction' && (
        <div className={classes.stepContent}>
          <Typography variant="h6" style={{ marginBottom: '16px' }}>
            Edit Interaction Activity
          </Typography>
          <Typography variant="body2" color="textSecondary">
            The chat interface will open automatically to help you modify this
            interaction activity. Your changes will be automatically saved as
            you work.
          </Typography>
        </div>
      )}

      <ChatInterface
        selectedData={selectedData}
        isModalOpen={isChatModalOpen}
        handleCloseModal={handleCloseChatModal}
        onStreamResponse={handleStreamResponse}
        onSendMessage={handleSendMessage}
        position={position}
        initialLoading={isInitialLoading}
        onInteractionUsed={() => {
          handleCloseChatModal()
          if (props.onInteractionUsed) {
            props.onInteractionUsed()
          }
        }}
        selectedElementData={selectedElementData}
        mode={mode}
        streamSource="InteractionBuilder"
        sessionId={sessionIdRef.current}
      />
    </div>
  )
}

export default withStyles(styles, { withTheme: true })(InteractionBuilder)
